import { Component, EventEmitter, inject, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import WorkspaceStore from '@app/store/workspace.store';
import { BillingSubscription } from '@interfaces/subscription';
import { Role } from '@interfaces/user';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@services/navigation.service';
import { UserService } from '@services/user.service';
import { UtilsService } from '@services/utils.service';
import { getTranslation } from '@shared/translate-helper';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-invite-members-modal',
  templateUrl: './invite-members-modal.component.html',
  styleUrl: './invite-members-modal.component.scss',
  standalone: false,
})
export class InviteMembersModalComponent {
  @Output() public onInviteTeamMember = new EventEmitter<void>();

  public selectedFormType: 'single' | 'multiple' = 'single';
  public isEmailValid = true;
  public showErrors = false;
  public userRoles: any[] = [];
  public addMemberForm: FormGroup;
  public multipleMembersForm: FormGroup;
  public multipleUsersSelectedRole!: Role;
  public addMemberSuccess = false;
  public readonly TRIAL_SEAT_LIMIT = 10;
  public workspaceStore: InstanceType<typeof WorkspaceStore> = inject(WorkspaceStore);

  constructor(
    public readonly userService: UserService,
    private readonly activeModal: NgbActiveModal,
    private readonly navigationService: NavigationService,
    private readonly utilsService: UtilsService,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private readonly spinner: NgxSpinnerService,
  ) {
    this.addMemberForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      role: ['', [Validators.required]],
    });

    // Form for multiple members, with two initial rows
    this.multipleMembersForm = this.fb.group({
      users: this.fb.array([this.createUsersFormGroup(), this.createUsersFormGroup()]),
    });
    getTranslation(this.translateService, ['client_admin', 'user']).subscribe((t: any) => {
      this.userRoles = [
        {
          name: t['client_admin'],
          value: Role.ClientAdmin,
        },
        {
          name: t['user'],
          value: Role.User,
        },
      ];
    });
  }

  closeDialog() {
    this.activeModal.close();
  }

  gotoSubscriptionPage() {
    this.navigationService.navigateToSubscription();
    this.activeModal.close();
  }

  changeFormType(type: 'single' | 'multiple') {
    this.selectedFormType = type;
  }

  addTeamMember() {
    if (this.addMemberForm.valid) {
      this.onInviteTeamMember.emit(this.addMemberForm.value);
    } else {
      this.showErrors = true;
    }
  }

  /* Add row in multiple members form */
  addRow() {
    this.users.push(this.createUsersFormGroup());
  }

  /* Remove row in multiple members form
   * @param index - index of the row to be removed
   */
  removeRow(index: number): void {
    if (this.users.length > 2) {
      this.users.removeAt(index);
    }
  }

  onSubmitMultipleUsers() {
    if (this.multipleMembersForm.valid) {
      console.log('Form submitted:', this.multipleMembersForm.value);
    }
  }

  formatDate(date: number | undefined, displayFormat: string): string {
    if (!date) return '';
    const newDate = new Date(date * 1000);
    return this.utilsService.formatDate(newDate, displayFormat);
  }

  get users(): FormArray {
    return this.multipleMembersForm.get('users') as FormArray;
  }

  get subscription(): (BillingSubscription & { subscriptionItem?: any }) | null {
    const subscription = this.userService.companySubscription();
    if (subscription) {
      return {
        ...subscription,
        subscriptionItem: subscription.items?.data[0] || null,
      };
    }
    return null;
  }

  private createUsersFormGroup(): FormGroup {
    return this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
    });
  }
}
